<template>
  <div class="resultados_tab">
    <!-- Header Section -->
    <div
      class="header_section_header is-flex is-flex-direction-row is-justify-content-space-between is-align-items-end mb-5 sticky-header">
      <div>
        <div class="header_title">Estado de resultados</div>
        <div class="header_description mb-4">
          Visualiza el estado de tu negocio en un solo lugar.
        </div>

        <!-- Sub-Navigation Filters -->
        <div class="sub_nav_filters">
          <button v-for="period in filterOptions" :key="period.id" class="filter_btn"
            :class="{ 'is-active': filterPeriod === period.id }" @click="updateFilterPeriod(period.id)">
            {{ period.label }}
          </button>
        </div>
      </div>

      <!-- Right Actions -->
      <div class="right_actions is-flex">
        <div class="calendar_picker is-flex is-align-items-center mr-3">
          <AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth" :disableNewerDates="true"
            :disabledFrom="getNow" formatLabelDate="MMMM YYYY" :alignRight="true" />
        </div>
        <button class="theme_btn_white is-flex is-align-items-center">
          <DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
          Exportar
        </button>
      </div>
    </div>

    <!-- Table Section -->
    <div class="underHeader">
      <FinancialResultsTable
        class="mt-6"
        title="Estado de Resultados"
        :rows="tableRows"
        :headers="getHeadersTable"
        :isLoading="isLoading"
        :selectedPeriodRange="selectedPeriodRange"
        :periodOptions="periodOptions"
        :filterPeriod="filterPeriod"
        :expandedRows="expandedRows"
        @update-period="updateSelectedPeriodRange"
        @toggle-childs="changeViewTotalBills">
        <template #header-actions>
          <button class="action_btn mr-2">
            <ChartLine class="mr-1" style="width: 16px; height: 16px;" />
            Ver Gráfico
          </button>
        </template>
      </FinancialResultsTable>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { PERIOD_TYPES, REPORT_DETAILS_CONFIG, FILTER_PERIOD_OPTIONS } from "../config/reportStructure";

import AnualCalendar from "@/components/classification/AnualCalendar";
import ChartLine from "@/components/svg/ChartLine.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";

export default {
  name: "ResultadosTab",
  components: {
    AnualCalendar,
    ChartLine,
    DownloadFile,
    FinancialResultsTable
  },
  data() {
    return {
      totalBillsExpanded: false,
      periodOptions: PERIOD_TYPES,
      filterOptions: FILTER_PERIOD_OPTIONS
    };
  },
  computed: {
    ...mapState('reportsFilters', ['endMonth', 'selectedPeriodRange']),
    ...mapState('financialReports', [
      'filterPeriod',
      'tableRowHeaders',
      'netSellsDetail',
      'maxCostsDetail',
      'maxExpensesDetail',
      'isLoading'
    ]),
    ...mapGetters('financialReports', {
      getHeadersTableFunc: 'getHeadersTable'
    }),
    endMonthProxy: {
      get() { return this.endMonth; },
      set(val) { this.updateEndMonth(val); }
    },
    getHeadersTable() {
      return this.getHeadersTableFunc();
    },
    tableRows() {
      const headers = this.tableRowHeaders;
      return Object.entries(headers)
        .filter(([, row]) => row.isShowed !== false)
        .map(([key, row]) => ({ ...row, id: key }));
    },
    expandedRows() {
      return this.totalBillsExpanded ? ['gastos_totales'] : [];
    },
    getNow() {
      return moment().toDate();
    },
    allDetails() {
      let ventasLabel = '';
      switch (this.filterPeriod) {
        case 'month':
          ventasLabel = 'MoM';
          break;
        case 'quarter':
          ventasLabel = 'QoQ';
          break;
        case 'ytd':
        case 'year':
          ventasLabel = 'YoY';
          break;
      }

      const headers = this.getHeadersTable;
      if (headers.length === 0) return [];

      const lastHeader = headers[headers.length - 1];
      const prevHeader = headers.length > 1 ? headers[headers.length - 2] : null;

      return REPORT_DETAILS_CONFIG.map(det => {
        let newValue = Math.round(
          this.tableRowHeaders[det.key]?.itemsDict?.[lastHeader] ?? 0
        ) + "%";

        if (det.key === 'ventas_netas') {
          const numerator = Number(this.tableRowHeaders[det.key]?.itemsDict?.[lastHeader] ?? 0);
          const denominator = prevHeader ? Number(this.tableRowHeaders[det.key]?.itemsDict?.[prevHeader] ?? 0) : 0;
          if (denominator !== 0) {
            newValue = Math.round((numerator / denominator - 1) * 100) + '% ' + ventasLabel;
          } else {
            newValue = '0% ' + ventasLabel;
          }
        }

        return {
          ...det,
          value: newValue
        };
      });
    },
    allSectionDetails() {
      const lastHeader = this.getHeadersTable[this.getHeadersTable.length - 1];
      return [
        [
          { label: 'Ticket Promedio', value: this.money(this.netSellsDetail?.ticket_average ?? 0) },
          { label: 'Cantidad de Clientes', value: this.netSellsDetail?.nro_counterparts ?? '0' }
        ],
        [
          { label: 'Cuenta', value: this.maxCostsDetail?.max_cost_account ?? '_' },
          { label: 'Contraparte', value: this.maxCostsDetail?.max_cost_counterpart ?? '_' }
        ],
        [
          { label: 'Cuenta', value: this.maxExpensesDetail?.max_expenses_account ?? '_' },
          { label: 'Contraparte', value: this.maxExpensesDetail?.max_expenses_counterpart ?? '_' }
        ],
        [
          { label: 'Antes de Impuestos', value: this.money(this.tableRowHeaders['utilidad_antes_de_impuestos']?.itemsDict[lastHeader] ?? 0) },
          { label: 'Margen Neto %', value: Math.round(this.tableRowHeaders['margen_neto_%']?.itemsDict[lastHeader] ?? 0) + '%' }
        ]
      ];
    },
    // Firma reactiva consolidada — un solo trigger para fetch aunque
    // varios filtros cambien en la misma transacción.
    fetchSignature() {
      return [this.filterPeriod, this.endMonth, this.selectedPeriodRange].join("|");
    },
  },
  mounted() {
    this.fetchReportData();
  },
  methods: {
    ...mapActions('financialReports', ['fetchReportData']),
    ...mapActions('reportsFilters', ['setPeriodRange']),
    ...mapMutations('reportsFilters', ['SET_END_MONTH']),
    ...mapMutations('financialReports', [
      'SET_FILTER_PERIOD',
      'TOGGLE_CHILD_ROWS'
    ]),
    updateFilterPeriod(period) {
      this.SET_FILTER_PERIOD(period);
    },
    updateEndMonth(date) {
      this.SET_END_MONTH(date);
    },
    updateSelectedPeriodRange(range) {
      this.setPeriodRange(range);
    },
    // Único toggle expandible en Resultados; el evento del componente trae
    // rowId pero solo gastos_totales lo emite porque es la única fila con
    // hasChilds. Si en el futuro se agregan más, expandir a un Set por id.
    changeViewTotalBills(rowId) {
      if (rowId !== 'gastos_totales') return;
      this.totalBillsExpanded = !this.totalBillsExpanded;
      this.TOGGLE_CHILD_ROWS(this.totalBillsExpanded);
    },
  },
  watch: {
    fetchSignature() {
      this.fetchReportData();
    },
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.resultados_tab {
  position: relative;
  padding-bottom: 40px;
  height: 100%;
}

.header_title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.sticky-header {
  // --tab-sticky-top lo publica DashboardReports midiendo .headerTop con
  // ResizeObserver. Mismo patrón que GastosTab/CostosTab/VentasTab.
  position: sticky;
  top: var(--tab-sticky-top, 193px);
  z-index: 4;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}


.sub_nav_filters {
  display: flex;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: fit-content;
}

.filter_btn {
  padding: 6px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &.is-active {
    background: #0d3c3f;
    color: #ffffff;
  }
}

.calendar_picker {
  background: #0d3c3f;
  color: white;
  border-radius: 8px;
  font-weight: 500;

  ::v-deep .calendar-label {
    color: white !important;
  }
}

.theme_btn_white {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

.action_btn {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

.has-gap-cards {
  gap: 16px;
}
</style>
